import React from 'react';
import { IconButton, useTheme } from '@mui/material';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { alpha } from '@mui/material/styles';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { exportActionTooltips } from '../../../shared/config/actionHelpCatalog';
import type { PipingSystem } from '../types';
import { getPipingSystemModelElementCount } from '../lib/modelSelection';

interface ModelSelectButtonProps {
  system: PipingSystem;
  compact?: boolean;
  modelSelectionBusy?: boolean;
  onSelectInModel?: (systems: PipingSystem[]) => void;
}

export default function ModelSelectButton({
  system,
  compact = false,
  modelSelectionBusy = false,
  onSelectInModel,
}: ModelSelectButtonProps) {
  const theme = useTheme();
  const elementCount = getPipingSystemModelElementCount(system);
  const disabled = !onSelectInModel || modelSelectionBusy || elementCount === 0;
  const ariaLabel = elementCount > 0
    ? `Select ${system.systemName} in Revit model, ${elementCount} element${elementCount === 1 ? '' : 's'}`
    : `${system.systemName} has no selectable Revit model elements`;

  return (
    <ActionHelpTooltip
      help={exportActionTooltips.selectSystemInModel}
      disabledHelp={exportActionTooltips.selectSystemInModelDisabled}
      disabled={disabled}
    >
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={(event) => {
          event.stopPropagation();
          if (!disabled) {
            onSelectInModel?.([system]);
          }
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        sx={{
          width: compact ? 28 : 30,
          height: compact ? 28 : 30,
          color: disabled ? theme.palette.action.disabled : theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <CenterFocusStrongIcon sx={{ fontSize: compact ? '0.95rem' : '1rem' }} />
      </IconButton>
    </ActionHelpTooltip>
  );
}
